import { useAuth0 } from '@auth0/auth0-react'
import { useEffect, useEffectEvent, useRef } from 'react'
import { useLocation } from 'react-router'
import { useAppSelector } from 'store/configureStore'
import {
  loadQFMessenger,
  QF_MESSENGER_AUDIENCE,
  QF_MESSENGER_SCOPE,
  reportMessengerTokenError,
} from './qfMessengerRuntime'

export const QFMessenger = () => {
  const { getAccessTokenSilently, isAuthenticated, isLoading, user } = useAuth0()
  const location = useLocation()
  const bootedUser = useRef<string | null>(null)
  const partyId = useAppSelector((state) => state.user.organizationContext?.id)
  const projectId = useAppSelector((state) => state.project.activeProject?.id)
  const bomItemId = useAppSelector(
    (state) => state.project.selectedBomItemPointers?.[0]?.id,
  )
  const workflowStage = useAppSelector(
    (state) =>
      state.project.activeProject?.processFlowState ||
      state.project.activeProject?.status,
  )

  const context: QFMessengerContext = {
    partyId: partyId ?? null,
    projectId: projectId ?? null,
    bomItemId: bomItemId ?? null,
    workflowStage: workflowStage ?? null,
    route: location.pathname,
  }

  const boot = useEffectEvent(() => {
    window.QFMessenger?.('boot', {
      getToken: async () => {
        try {
          return await getAccessTokenSilently({
            authorizationParams: {
              audience: QF_MESSENGER_AUDIENCE,
              scope: QF_MESSENGER_SCOPE,
            },
          })
        } catch (error) {
          reportMessengerTokenError(error)
          return null
        }
      },
      context,
    })
  })

  const updateContext = useEffectEvent(() => {
    window.QFMessenger?.('updateContext', context)
  })

  useEffect(() => {
    loadQFMessenger()
  }, [])

  useEffect(() => {
    if (!isAuthenticated) bootedUser.current = null
  }, [isAuthenticated])

  useEffect(() => {
    if (isLoading || !isAuthenticated || !user?.sub) return
    if (bootedUser.current === user.sub) return

    bootedUser.current = user.sub
    boot()
  }, [isAuthenticated, isLoading, user?.sub])

  useEffect(() => {
    if (!isAuthenticated || bootedUser.current !== user?.sub) return
    updateContext()
  }, [
    bomItemId,
    isAuthenticated,
    location.pathname,
    partyId,
    projectId,
    user?.sub,
    workflowStage,
  ])

  return null
}
